import { Box } from '@mui/material';
import React from 'react';
import { useCommonWebTranslation } from '../hooks';
const visuallyHiddenStyle = {
    position: 'absolute',
    left: 0,
    top: 0,
    width: 0,
    height: 0,
    overflow: 'hidden',
};
/**
 * Renders a responsive, reorderable grid of cards with optional editing capability.
 * @template T - Type extending an object with a string or number `id`.
 * @param {CardGridProps<T>} props - Props for rendering the CardGrid.
 * @param {object} props.sx - Optional custom styling or responsive styles.
 * @param {T[]} props.cards - List of cards to render.
 * @param {number | object} props.columns - Grid column count or responsive column mapping.
 * @param {number} props.gap - Gap between grid items.
 * @param {boolean} props.editable - Enables drag-and-drop functionality.
 * @param {(props: CardInGridProps, card: T, index: number) => React.ReactElement} props.renderCard - Function that renders a card.
 * @param {React.ReactNode} props.addCardElement - Optional element shown as "Add card".
 * @param {(cards: T[]) => void} props.onChange - Callback invoked after cards are reordered.
 * @returns {React.ReactElement} The rendered CardGrid component.
 */
export function CardGrid({ sx, cards, columns = 3, gap = 2, editable, renderCard, addCardElement, onChange, }) {
    const { t } = useCommonWebTranslation();
    const root = React.useRef(null);
    const [movingId, setMovingId] = React.useState();
    const [draggingIndex, setDraggingIndex] = React.useState();
    const [draggingNewIndex, setDraggingNewIndex] = React.useState();
    const [ariaNewIndex, setAriaNewIndex] = React.useState();
    // Moves the card to a new position
    const moveCard = (newIndex, currentIndex) => {
        if (newIndex === currentIndex)
            return; // Skip if no actual move
        const newCardList = [...cards];
        const [movedCard] = newCardList.splice(currentIndex, 1);
        newCardList.splice(newIndex, 0, movedCard);
        onChange === null || onChange === void 0 ? void 0 : onChange(newCardList);
        setAriaNewIndex(newIndex);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { "aria-live": 'assertive', role: 'alert', style: visuallyHiddenStyle }, ariaNewIndex != null ? t('cardGrid.newPositionInList', { index: ariaNewIndex + 1, total: cards.length }) : ''),
        React.createElement(Box, { ref: root, role: 'grid', sx: [
                { display: 'grid', gridTemplateColumns: mapGridTemplateColumns(columns), gap: gap },
                ...(Array.isArray(sx) ? sx : [sx]),
            ] },
            cards.map((card, index) => {
                // Adjust `order` to reflect dragging changes
                let order = index;
                if (draggingIndex !== undefined && draggingNewIndex !== undefined) {
                    if (index > draggingIndex && index <= draggingNewIndex) {
                        // Shift order up for items between draggingIndex and draggingNewIndex
                        order = index - 1;
                    }
                    else if (index < draggingIndex && index >= draggingNewIndex) {
                        // Shift order down for items between draggingNewIndex and draggingIndex
                        order = index + 1;
                    }
                    else if (index === draggingIndex) {
                        // Set the dragged item to the target index position
                        order = draggingNewIndex;
                    }
                }
                const cardInGridProps = {
                    moving: card.id === movingId,
                    onPointerDragStart: editable
                        ? () => {
                            setMovingId(undefined);
                            setDraggingIndex(index);
                            return true;
                        }
                        : undefined,
                    onPointerDragMove: editable
                        ? (point) => {
                            const newIndex = calculateDraggingNewIndex(root, point, cards.length);
                            setDraggingNewIndex(newIndex);
                            setAriaNewIndex(newIndex !== null && newIndex !== void 0 ? newIndex : index);
                        }
                        : undefined,
                    onPointerDragEnd: editable
                        ? (point) => {
                            const newIndex = calculateDraggingNewIndex(root, point, cards.length);
                            setDraggingIndex(undefined);
                            setDraggingNewIndex(undefined);
                            if (newIndex !== undefined && newIndex !== index) {
                                moveCard(newIndex, index);
                            }
                        }
                        : undefined,
                    onPointerDragCancel: editable
                        ? () => {
                            setDraggingIndex(undefined);
                            setDraggingNewIndex(undefined);
                            setAriaNewIndex(undefined);
                        }
                        : undefined,
                    onSetMoving: editable
                        ? (moving) => {
                            setMovingId(moving ? card.id : undefined);
                        }
                        : undefined,
                    onMoveCard: editable
                        ? (direction) => {
                            const newIndex = direction === 'left' ? index - 1 : index + 1;
                            if (newIndex >= 0 && newIndex < cards.length) {
                                moveCard(newIndex, index);
                            }
                        }
                        : undefined,
                };
                return (React.createElement(Box, { key: card.id, sx: { order }, role: 'gridcell' }, renderCard(cardInGridProps, card, index)));
            }),
            editable && addCardElement && React.createElement(Box, { sx: { order: cards.length } }, addCardElement))));
}
/**
 * Converts a column configuration (number or responsive object) into a CSS grid template.
 * @param {CardGridProps<never>['columns']} columns - Either a number of columns or a responsive object mapping breakpoints to column counts.
 * @returns {string | Record<string, string>} A grid template string or a responsive object of template strings.
 * @throws {Error} If the column type is not a number or object.
 */
function mapGridTemplateColumns(columns) {
    if (typeof columns === 'number') {
        return mapGridTemplateColumn(columns); // If columns is a number, format a simple grid
    }
    if (typeof columns === 'object') {
        // Ensure the type aligns with an object structure before mapping
        return Object.fromEntries(Object.entries(columns).map(([key, value]) => [key, mapGridTemplateColumn(value)]));
    }
    throw new Error('Invalid columns type'); // Handle unexpected types
}
// Helper function to create a column template string
/**
 * Generates a CSS grid template string for the given number of columns.
 * @param {number} columns - The number of columns.
 * @returns {string} A grid template string using `1fr` units, e.g., "1fr 1fr 1fr".
 */
function mapGridTemplateColumn(columns) {
    return '1fr '.repeat(columns).trim();
}
// Calculate the new index for dragging
/**
 * Calculates the new card index in a CSS grid layout based on a pointer's position.
 * @param {React.RefObject<React.HTMLDivElement | null>} root - Ref to the grid container element.
 * @param {{ x: number; y: number }} point - The current pointer position.
 * @param {number} point.x - Horizontal coordinate of the pointer.
 * @param {number} point.y - Vertical coordinate of the pointer.
 * @param {number} numberOfCards - Total number of cards in the grid.
 * @returns {number | undefined} The new index if within bounds, otherwise undefined.
 */
function calculateDraggingNewIndex(root, point, numberOfCards) {
    const div = root.current;
    if (div) {
        const rect = div.getBoundingClientRect();
        if (point.x >= rect.x && point.y >= rect.y && point.x < rect.x + rect.width && point.y < rect.y + rect.height) {
            const style = window.getComputedStyle(div);
            const columns = style.gridTemplateColumns.split(' ').map(parseFloat);
            const rows = style.gridTemplateRows.split(' ').map(parseFloat);
            const columnGap = parseFloat(style.columnGap);
            const rowGap = parseFloat(style.rowGap);
            const column = calculateGridIndex(columns, columnGap, point.x - rect.x);
            const row = calculateGridIndex(rows, rowGap, point.y - rect.y);
            const newIndex = row * columns.length + column;
            return newIndex >= 0 && newIndex < numberOfCards ? newIndex : undefined;
        }
    }
    return undefined;
}
/**
 * Helper function to calculate grid position based on x or y position.
 * @param {number[]} sizes - The size of each grid cell (row or column).
 * @param {number} gap - The gap between grid cells.
 * @param {number} position - The pointer position relative to the grid.
 * @returns {number} The calculated grid index based on the position.
 */
function calculateGridIndex(sizes, gap, position) {
    let accumulatedSize = 0;
    for (let i = 0; i < sizes.length; i++) {
        accumulatedSize += sizes[i] + (i === 0 || i === sizes.length - 1 ? gap / 2 : gap);
        if (position < accumulatedSize) {
            return i;
        }
    }
    return sizes.length - 1;
}
